import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { groupBy } from 'lodash';
import classNames from 'classnames';

import useScreen from 'hooks/useScreen';
import Placeholder from 'components/consumer/Placeholder';
import PDPActiveFabric from 'components/consumer/PDP/PDPActiveFabric';
import PDPDecideLaterPromo from 'components/consumer/PDP/PDPDecideLaterPromo';
import PDPUpholsterySortBy from 'components/consumer/PDP/PDPUpholsterySortBy';
import MaterialThumbnailGroup from 'components/consumer/MaterialThumbnailGroup';
import PDPUpholsteryFilters from 'components/consumer/PDP/PDPUpholsteryFilters';
import PDPFabricDetailSidebar from 'components/consumer/PDP/PDPFabricDetailSidebar';
import PDPFreeSwatchKitButton from 'components/consumer/PDP/PDPFreeSwatchKitButton';
import PDPOrderSwatchesSidebar from 'components/consumer/PDP/PDPOrderSwatchesSidebar';
import PDPUpholsteryColorFilters from 'components/consumer/PDP/PDPUpholsteryColorFilters';
import {
  PDP_CUSTOMIZE_TAB_ID,
  PDP_READYTOSHIP_TAB_ID,
  PDP_DECIDELATER_TAB_ID,
  FABRIC_PRODUCT_OPTION_ID,
  PDP_NAMED_FILTERS,
  PANTONE_FABRIC_CONFIG,
} from 'commons/constants';

import { materialPropType, productFilterTypes } from 'types/PDPTypes';
import useFabricDetails from 'data-hooks/useFabricDetails';
import useIsTouchDevice from 'hooks/useIsTouchDevice';
import usePdpIsModularBYOProduct from 'components/consumer/PDP/state/context/usePdpIsModularBYOProduct';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import CtaButton from 'components/essentials/Cta/CtaButton';
import MaterialThumbnailGroupPlaceholder from 'components/consumer/MaterialThumbnailGroup/MaterialThumbnailGroupPlaceholder';
import FlyoutContainer from '../../FlyoutContainer';
import PDPSelectUpholsteryDecideLater from './PDPSelectUpholsteryDecideLater';

const { pantoneFabricSkus } = PANTONE_FABRIC_CONFIG;

const tiers = {
  1: {
    title: 'Tier 1',
    desc:
      'Readily available fabrics that bring an eye-catching look to your piece at a great value.',
  },
  2: {
    title: 'Tier 2',
    desc:
      'Kick things up a notch with fabrics that show off more rich colors, textures, and durability.',
  },
  3: {
    title: 'Tier 3',
    desc:
      'Got an active home? Check out these fabrics that showcase top notch performance finishes while still looking stylish.',
  },
  4: {
    title: 'Tier 4',
    desc:
      'As luxe as it gets, this tier includes our branded performance fabrics and stain resistant finish fabrics, ready to take on all life throws its way in sophisticated style.',
  },
};

const NUMBER_OF_SWATCHES_PER_ROW_ON_DESKTOP = 9;

const MAX_SWATCHES_BEFORE_SHOW_ALL_BUTTON =
  3 * NUMBER_OF_SWATCHES_PER_ROW_ON_DESKTOP;

const SORT_BY_OPTIONS = {
  POPULARITY: 'popularity',
  FAMILY: 'family',
  TIER: 'tier',
};

const moveMaterialToSecondPosition = (materials, materialSku) => {
  if (materials?.length && materialSku && materials[0]?.sku !== materialSku) {
    const filteredMaterials = materials.filter(
      ({ sku }) => sku !== materialSku
    );

    const material = materials.find(({ sku }) => sku === materialSku);
    const [firstMaterial, ...restMaterials] = filteredMaterials;

    return [firstMaterial, material, ...restMaterials];
  }

  return materials;
};

const PDPSelectUpholsteryUI = ({
  activeColorFilters,
  activeNamedFilters,
  allMaterialsLength,
  clearAllFilters,
  disabledMaterialIds,
  fabricFavProps,
  filterCounts = {},
  flashSaleOptionIds = [],
  isClearanceFabricAvailable,
  isEditOrder = false,
  isSelectedOptionsQuickship,
  loading,
  materials,
  mode = 'pdp',
  onMoreMaterialsClick,
  onOptionValueHover,
  onOptionValueSelection,
  selectedMaterial,
  selectedOptionsStockQty,
  toggleColorFilter,
  toggleNamedFilter,
  upholsteryFilteredEvent,
  upholsteryOptionId,
  upholsteryOptionName,
}) => {
  const { isDesktop, isMobile } = useScreen();
  const pdpIsModularBYOProduct = usePdpIsModularBYOProduct();

  const [selectedSortOption, setSelectedSortOption] = useState(
    SORT_BY_OPTIONS.POPULARITY
  );

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();

  const [isOrderSwatchesSidebarOpen, setIsOrderSwatchesSidebarOpen] = useState(
    false
  );

  const [galleryType, setGalleryType] = useState('');
  const [sortByFlyOutContent, setSortByFlyOutContent] = useState({});
  const [isSortByFlyOutVisible, setIsSortByFlyOutVisible] = useState(false);

  const {
    data: { selectedFabric },
  } = useFabricDetails(selectedMaterial?.materialId);

  const isTouchDevice = useIsTouchDevice();

  const isPdpMode = mode === 'pdp';

  const materialsGroupedByFamily = useMemo(
    () =>
      selectedSortOption === SORT_BY_OPTIONS.FAMILY
        ? groupBy(materials, 'groupName')
        : {},
    [selectedSortOption, materials]
  );

  const materialsGroupedByTier = useMemo(
    () =>
      selectedSortOption === SORT_BY_OPTIONS.TIER
        ? groupBy(materials, SORT_BY_OPTIONS.TIER)
        : {},
    [selectedSortOption, materials]
  );

  const isQuickview = mode === 'quickview';
  const quickviewNumberOfFabricsToShow = isMobile ? 5 : 8;

  const limitNumberShownForQuickview =
    isQuickview && allMaterialsLength > quickviewNumberOfFabricsToShow;

  const ungroupedMaterialsArray = useMemo(() => {
    const materialsPantoneFabricAsSecond = isQuickview
      ? moveMaterialToSecondPosition(materials, pantoneFabricSkus?.[0])
      : materials;

    if (limitNumberShownForQuickview) {
      return materialsPantoneFabricAsSecond.slice(
        0,
        quickviewNumberOfFabricsToShow
      );
    }

    return materialsPantoneFabricAsSecond;
  }, [
    isQuickview,
    limitNumberShownForQuickview,
    materials,
    quickviewNumberOfFabricsToShow,
  ]);

  const moreMaterialsCount = limitNumberShownForQuickview
    ? allMaterialsLength - quickviewNumberOfFabricsToShow
    : undefined;

  const ungroupedMaterialsLength = ungroupedMaterialsArray?.length ?? 0;

  const [
    hasShowAllFabricsButtonBeenClicked,
    setHasShowAllFabricsButtonBeenClicked,
  ] = useState(false);

  const isAllFabricsShown = !activeNamedFilters.length;

  const showShowAllFabricsButton =
    isDesktop &&
    isAllFabricsShown &&
    !loading &&
    !hasShowAllFabricsButtonBeenClicked &&
    ungroupedMaterialsLength > MAX_SWATCHES_BEFORE_SHOW_ALL_BUTTON;

  const ungroupedMaterialsToShow = useMemo(
    () =>
      showShowAllFabricsButton
        ? ungroupedMaterialsArray.slice(0, MAX_SWATCHES_BEFORE_SHOW_ALL_BUTTON)
        : ungroupedMaterialsArray,
    [ungroupedMaterialsArray, showShowAllFabricsButton]
  );

  const getFabricFavInfoToShow = useCallback(
    upholsteryOptionNameVal => {
      const tapOrClick = isTouchDevice ? 'tap' : 'click';

      return upholsteryOptionNameVal === 'Fabric'
        ? `Double-${tapOrClick} a fabric to add to your favorites`
        : upholsteryOptionNameVal === 'Leather'
        ? `Double-${tapOrClick} a leather to add to your favorites`
        : `Double tap to favorite`;
    },
    [isTouchDevice]
  );

  const clearAllFiltersButton = useMemo(() => {
    const noActiveNamedFilters = !activeNamedFilters.length;
    const noActiveColorFilters = activeColorFilters.length === 0;
    const noActiveFilters = noActiveNamedFilters && noActiveColorFilters;

    return (
      <button
        type="button"
        className={classNames('text-xs font-bold p-[5px] underline', {
          'text-gray-light4 pointer-events-none': noActiveFilters,
          'text-red hover:text-red-dark': !noActiveFilters,
        })}
        onClick={() => {
          if (!noActiveFilters) {
            clearAllFilters();
          }
        }}
      >
        Clear All
      </button>
    );
  }, [clearAllFilters, activeNamedFilters, activeColorFilters.length]);

  const onToggleFlyOut = useCallback((content, isVisible) => {
    setSortByFlyOutContent(content);
    setIsSortByFlyOutVisible(isVisible);
  }, []);

  const flyoutContainerContent = useMemo(
    () =>
      isSortByFlyOutVisible && (
        <FlyoutContainer
          top={
            sortByFlyOutContent.top > 300
              ? sortByFlyOutContent.top - 300
              : sortByFlyOutContent.top
          }
          title={sortByFlyOutContent.title}
          content={sortByFlyOutContent.content}
          subTitle={sortByFlyOutContent.subTitle}
          imageUrl={sortByFlyOutContent.imageUrl}
          titleInfo={sortByFlyOutContent.titleInfo}
          titleExtra={sortByFlyOutContent.titleExtra}
          subSubTitle={sortByFlyOutContent.subSubTitle}
          onClose={() => setIsSortByFlyOutVisible(false)}
          isOpen={isSortByFlyOutVisible}
        />
      ),
    [isSortByFlyOutVisible, sortByFlyOutContent]
  );

  const sortByFamilyMaterialThumbnails = useMemo(
    () => (
      <div className="p-6 max-h-[472px] overflow-y-auto bg-gray-light9 border border-solid border-gray-light7">
        {Object.keys(materialsGroupedByFamily)
          .sort()
          .map(groupName => (
            <div key={groupName} className="mb-8">
              <div className="text-sm font-bold text-gray">{groupName}</div>
              <MaterialThumbnailGroup
                disabledMaterialIds={disabledMaterialIds}
                fabricFavProps={fabricFavProps}
                flashSaleOptionIds={flashSaleOptionIds}
                isEditOrder={isEditOrder}
                isModularBundleProduct={pdpIsModularBYOProduct}
                materials={materialsGroupedByFamily[groupName]}
                onClick={onOptionValueSelection}
                onHover={onOptionValueHover}
                onToggleFlyOut={onToggleFlyOut}
                optionId={upholsteryOptionId}
                selectedMaterial={selectedMaterial}
              />
            </div>
          ))}
      </div>
    ),
    [
      disabledMaterialIds,
      fabricFavProps,
      flashSaleOptionIds,
      isEditOrder,
      materialsGroupedByFamily,
      onOptionValueHover,
      onOptionValueSelection,
      onToggleFlyOut,
      pdpIsModularBYOProduct,
      selectedMaterial,
      upholsteryOptionId,
    ]
  );

  const sortByTierMaterialThumbnails = useMemo(
    () => (
      <div>
        {Object.keys(materialsGroupedByTier).map(tierId => {
          const tier = tiers[tierId];
          return tier ? (
            <div key={tierId} className="mt-8">
              <div className="text-sm font-bold text-gray">{tier.title}</div>

              <div className="text-xs text-gray mb-[1.2rem]">{tier.desc}</div>

              <MaterialThumbnailGroup
                disabledMaterialIds={disabledMaterialIds}
                fabricFavProps={fabricFavProps}
                flashSaleOptionIds={flashSaleOptionIds}
                isEditOrder={isEditOrder}
                isModularBundleProduct={pdpIsModularBYOProduct}
                materials={materialsGroupedByTier[tierId]}
                onClick={onOptionValueSelection}
                onHover={onOptionValueHover}
                optionId={upholsteryOptionId}
                selectedMaterial={selectedMaterial}
              />
            </div>
          ) : (
            <div />
          );
        })}
      </div>
    ),
    [
      materialsGroupedByTier,
      disabledMaterialIds,
      fabricFavProps,
      flashSaleOptionIds,
      isEditOrder,
      pdpIsModularBYOProduct,
      onOptionValueSelection,
      onOptionValueHover,
      upholsteryOptionId,
      selectedMaterial,
    ]
  );

  const onCloseCallback = useCallback(() => {
    setGalleryType('');
    setSidebarOpen(false);
  }, []);

  const materialThumbnailVariant =
    mode === 'quickview'
      ? 'PDPSelectUpholsteryUI--quickview'
      : 'PDPSelectUpholsteryUI';

  return (
    <div
      className={classNames('relative', {
        '[border-bottom:1px_solid_#e5e5e5] mb-2 pb-8': mode !== 'quickview',
      })}
    >
      <div>
        {pdpSelectedCustomizerTab === PDP_DECIDELATER_TAB_ID ? (
          <PDPSelectUpholsteryDecideLater />
        ) : null}

        {mode !== 'quickview' &&
        upholsteryOptionId === FABRIC_PRODUCT_OPTION_ID &&
        !isMobile ? (
          <div className="items-center flex justify-between w-full mb-4">
            <h4 className="text-base font-bold">Filter by color:</h4>

            <PDPUpholsteryColorFilters
              activeColorFilters={activeColorFilters}
              toggleColorFilter={updatedColor => {
                toggleColorFilter(updatedColor);

                upholsteryFilteredEvent(
                  activeNamedFilters,
                  updatedColor,
                  selectedSortOption
                );
              }}
            />
          </div>
        ) : (
          <div />
        )}

        {pdpSelectedCustomizerTab === PDP_CUSTOMIZE_TAB_ID ||
        pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID ? (
          <div>
            {!!isPdpMode &&
            pdpSelectedCustomizerTab === PDP_CUSTOMIZE_TAB_ID ? (
              upholsteryOptionName === 'Leather' ? (
                <div className="text-sm -mt-8 mx-0 mb-8 font-bold">
                  {getFabricFavInfoToShow('Leather')}
                </div>
              ) : (
                <>
                  {isMobile && (
                    <div className="flex justify-between my-1">
                      <h4 className="text-base font-bold">Filter by color</h4>
                      {clearAllFiltersButton}
                    </div>
                  )}
                  <div className="mt-0 mx-0 space-y-2 max-[1023px]:-ml-10 max-[1023px]:-mr-5 [&::-webkit-scrollbar]:max-[1023px]:hidden max-[1023px]:pb-6">
                    {isMobile && (
                      <PDPUpholsteryColorFilters
                        activeColorFilters={activeColorFilters}
                        showRowInMobile
                        toggleColorFilter={color => {
                          toggleColorFilter(color);

                          upholsteryFilteredEvent(
                            activeNamedFilters,
                            color,
                            selectedSortOption
                          );
                        }}
                        showMobileColorFilter
                      />
                    )}

                    <PDPUpholsteryFilters
                      clearAllFiltersButton={clearAllFiltersButton}
                      filtersCollectionVisible={activeNamedFilters}
                      setFiltersCollectionVisible={updatedFilterType => {
                        toggleNamedFilter(updatedFilterType);
                        upholsteryFilteredEvent(
                          updatedFilterType,
                          activeColorFilters,
                          selectedSortOption
                        );
                      }}
                      filterCounts={filterCounts}
                      loading={loading}
                      showFabricFavs={!!fabricFavProps.actions.setOptionAsFav}
                      isClearanceFabricAvailable={isClearanceFabricAvailable}
                      fabricFavInfoToShow={
                        activeNamedFilters.length === 1 &&
                        activeNamedFilters[0] === PDP_NAMED_FILTERS.FAVORITES
                          ? getFabricFavInfoToShow(upholsteryOptionName)
                          : ''
                      }
                      variant="PDPSelectUpholstery"
                    />

                    {!loading && ungroupedMaterialsArray.length === 0 && (
                      <span className="text-xs font-bold text-gray">
                        Try removing a filter to see more fabric options
                      </span>
                    )}
                  </div>
                </>
              )
            ) : (
              <div />
            )}

            {mode !== 'quickview' &&
              upholsteryOptionId === FABRIC_PRODUCT_OPTION_ID && (
                <div className="hidden sm:block">
                  <PDPUpholsterySortBy
                    selectedOption={selectedSortOption}
                    setSelectedOption={updatedSortOption => {
                      setSelectedSortOption(updatedSortOption);

                      upholsteryFilteredEvent(
                        activeNamedFilters,
                        activeColorFilters,
                        updatedSortOption
                      );
                    }}
                  />
                </div>
              )}

            <div className="flex flex-row justify-between">
              <PDPActiveFabric
                isEditOrder={isEditOrder}
                isModularBundleProduct={pdpIsModularBYOProduct}
                isSelectedOptionsQuickship={
                  pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID &&
                  isSelectedOptionsQuickship
                }
                loading={loading}
                onClick={() => {
                  setGalleryType('FabricMultiImg');
                  setSidebarOpen(true);
                }}
                selectedOption={selectedMaterial}
                selectedOptionsStockQty={selectedOptionsStockQty}
                size={mode === 'quickview' ? 'xs' : undefined}
                showInfoIcon={!!selectedFabric}
              />

              {!!isPdpMode &&
              pdpSelectedCustomizerTab !== PDP_READYTOSHIP_TAB_ID &&
              !isEditOrder ? (
                loading ? (
                  <Placeholder
                    type="rect"
                    loading
                    style={{
                      height: '43px',
                      width: '152px',
                      borderRadius: '43px',
                    }}
                  />
                ) : (
                  <div className="mb-2">
                    <PDPFreeSwatchKitButton
                      styling="rainbow-button"
                      onClick={() => {
                        setIsOrderSwatchesSidebarOpen(true);
                      }}
                    />

                    <PDPOrderSwatchesSidebar
                      show={isOrderSwatchesSidebarOpen}
                      onClose={() => {
                        setIsOrderSwatchesSidebarOpen(false);
                      }}
                      closeSlidePanel={() => {
                        setIsOrderSwatchesSidebarOpen(false);
                      }}
                    />
                  </div>
                )
              ) : (
                <div />
              )}
            </div>

            {selectedSortOption === SORT_BY_OPTIONS.FAMILY ? (
              <>
                {flyoutContainerContent}
                {sortByFamilyMaterialThumbnails}
              </>
            ) : selectedSortOption === SORT_BY_OPTIONS.TIER ? (
              sortByTierMaterialThumbnails
            ) : (
              <div
                className={classNames(
                  'max-[1023px]:-ml-5 max-[1023px]:-mr-5 max-[1023px]:md:ml-[-3.333vw] max-[1023px]:md:mr-[-3.333vw]',
                  {
                    'max-[1023px]:md:!m-0': mode === 'quickview',
                  }
                )}
              >
                {loading ? (
                  <MaterialThumbnailGroupPlaceholder
                    variant={materialThumbnailVariant}
                  />
                ) : (
                  <MaterialThumbnailGroup
                    disabledMaterialIds={disabledMaterialIds}
                    enableFlyout={mode === 'pdp'}
                    fabricFavProps={fabricFavProps}
                    flashSaleOptionIds={flashSaleOptionIds}
                    isEditOrder={isEditOrder}
                    isModularBundleProduct={pdpIsModularBYOProduct}
                    materials={ungroupedMaterialsToShow}
                    moreMaterialsCount={moreMaterialsCount}
                    onClick={onOptionValueSelection}
                    onHover={onOptionValueHover}
                    onMoreMaterialsClick={onMoreMaterialsClick}
                    optionId={upholsteryOptionId}
                    selectedMaterial={selectedMaterial}
                    showMoreMaterialsOnDesktop={mode === 'quickview'}
                    variant={materialThumbnailVariant}
                  />
                )}

                {showShowAllFabricsButton && (
                  <div className="relative pt-6 mt-[-52px] bg-white bg-opacity-70">
                    <div className="bg-white px-[6px]">
                      <CtaButton
                        block
                        color="brand"
                        onClick={() => {
                          setHasShowAllFabricsButtonBeenClicked(true);
                        }}
                        spacing="sm"
                        styling="outline-button"
                      >
                        Show All Fabrics
                      </CtaButton>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!!isPdpMode &&
            pdpSelectedCustomizerTab !== PDP_READYTOSHIP_TAB_ID &&
            !isEditOrder &&
            isDesktop ? (
              <div className="mt-4 mx-0 mb-0">
                <PDPDecideLaterPromo />
              </div>
            ) : (
              <div />
            )}
          </div>
        ) : (
          <div />
        )}
      </div>

      <PDPFabricDetailSidebar
        isOpen={!!selectedMaterial?.materialId && isSidebarOpen}
        onClose={onCloseCallback}
        fabricId={selectedMaterial?.materialId}
        mode={mode}
        galleryType={galleryType}
        mobileHideHeader
      />
    </div>
  );
};

PDPSelectUpholsteryUI.propTypes = {
  activeNamedFilters: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  allMaterialsLength: PropTypes.number,
  disabledMaterialIds: PropTypes.arrayOf(PropTypes.number),
  fabricFavProps: PropTypes.object,
  filterCounts: productFilterTypes,
  flashSaleOptionIds: PropTypes.arrayOf(PropTypes.number),
  isClearanceFabricAvailable: PropTypes.bool,
  isEditOrder: PropTypes.bool,
  isSelectedOptionsQuickship: PropTypes.bool,
  loading: PropTypes.bool,
  materials: PropTypes.arrayOf(materialPropType),
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  onMoreMaterialsClick: PropTypes.func,
  onOptionValueHover: PropTypes.func,
  onOptionValueSelection: PropTypes.func,
  activeColorFilters: PropTypes.array,
  selectedMaterial: materialPropType,
  selectedOptionsStockQty: PropTypes.number,
  toggleColorFilter: PropTypes.func,
  toggleNamedFilter: PropTypes.func,
  upholsteryFilteredEvent: PropTypes.func,
  upholsteryOptionId: PropTypes.number,
  upholsteryOptionName: PropTypes.string,
};

export default PDPSelectUpholsteryUI;
